import React, { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';

/**
 * InsuranceLogo - Zeigt das Logo einer Versicherung mittels Clearbit Logo API
 * 
 * Die Clearbit Logo API ist kostenlos und benötigt keine API-Keys.
 * Format: https://logo.clearbit.com/{domain}
 */

// Mapping von bekannten deutschen Versicherungen zu deren Domains
// Clearbit funktioniert besser mit .com Domains für internationale Firmen
const insuranceDomains = {
  // Große internationale Versicherungen (mit .com)
  'allianz': 'allianz.com',
  'axa': 'axa.com',
  'generali': 'generali.com',
  'zurich': 'zurich.com',
  'helvetia': 'helvetia.com',
  'munich re': 'munichre.com',
  'talanx': 'talanx.com',
  'hannover re': 'hannover-re.com',
  
  // Deutsche Versicherungen
  'huk': 'huk.de',
  'huk-coburg': 'huk.de',
  'huk24': 'huk24.de',
  'ergo': 'ergo.de',
  'debeka': 'debeka.de',
  'devk': 'devk.de',
  'lvm': 'lvm.de',
  'provinzial': 'provinzial.de',
  'signal iduna': 'signal-iduna.de',
  'signal-iduna': 'signal-iduna.de',
  'vgh': 'vgh.de',
  'württembergische': 'wuerttembergische.de',
  'r+v': 'ruv.de',
  'ruv': 'ruv.de',
  'gothaer': 'gothaer.de',
  'continentale': 'continentale.de',
  'volkswohl bund': 'volkswohl-bund.de',
  'nürnberger': 'nuernberger.de',
  'nuernberger': 'nuernberger.de',
  'hdl': 'hdl.de',
  'kravag': 'kravag.de',
  'vhv': 'vhv.de',
  'itzehoer': 'itzehoer.de',
  'sparkassen versicherung': 'sparkassenversicherung.de',
  'sv sparkassen': 'sparkassenversicherung.de',
  'sv versicherung': 'sparkassenversicherung.de',
  'concordia': 'concordia.de',
  'adac': 'adac.de',
  'örag': 'oerag.de',
  'oerag': 'oerag.de',
  'roland': 'roland-rechtsschutz.de',
  'arag': 'arag.de',
  'das': 'das.de',
  'd.a.s.': 'das.de',
  'cosmos': 'cosmosdirekt.de',
  'cosmosdirekt': 'cosmosdirekt.de',
  'direct line': 'directline.de',
  'admiral': 'admiral.de',
  'da direkt': 'da-direkt.de',
  'verti': 'verti.de',
  'europa': 'europa.de',
  'basler': 'basler.de',
  'barmenia': 'barmenia.de',
  'alte leipziger': 'alte-leipziger.de',
  'mecklenburgische': 'mecklenburgische.de',
  'vgw': 'vgw.de',
  'westfälische provinzial': 'provinzial.de',
  'bayerische': 'diebayerische.de',
  'die bayerische': 'diebayerische.de',
  'wgv': 'wgv.de',
  'janitos': 'janitos.de',
  'interrisk': 'interrisk.de',
  'versicherungskammer bayern': 'vkb.de',
  'vkb': 'vkb.de',
  'öffentliche': 'oeffentliche.de',
  'mannheimer': 'mannheimer.de',
  'rheinland': 'rheinland-versicherungsgruppe.de',
  'sparkasse': 'sparkassenversicherung.de',
  'münchener verein': 'muenchener-verein.de',
};

/**
 * Versucht eine Domain aus dem Versicherungsnamen zu extrahieren
 * Probiert verschiedene Varianten
 */
const getDomainFromName = (name) => {
  if (!name) return null;
  
  const normalizedName = name.toLowerCase().trim();
  
  // Direkte Übereinstimmung prüfen
  for (const [key, domain] of Object.entries(insuranceDomains)) {
    if (normalizedName.includes(key)) {
      return domain;
    }
  }
  
  // Versuche den ersten relevanten Teil des Firmennamens
  // z.B. "Allianz Versicherungs-AG" -> "allianz.com"
  const words = normalizedName
    .replace(/versicherung|versicherungs|aktiengesellschaft|ag|gmbh|service|rechtsschutz/gi, '')
    .split(/[\s-]+/)
    .filter(w => w.length > 2);
  
  if (words.length > 0) {
    const firstWord = words[0]
      .replace(/[äöüß]/g, (match) => {
        const replacements = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' };
        return replacements[match] || match;
      })
      .replace(/[^a-z0-9]/g, '');
    
    if (firstWord.length > 2) {
      // Probiere zuerst .com, dann .de
      return `${firstWord}.com`;
    }
  }
  
  return null;
};

/**
 * Generiert alternative Domains für Fallback
 */
const getAlternativeDomains = (name) => {
  if (!name) return [];
  
  const normalizedName = name.toLowerCase().trim();
  const alternatives = [];
  
  // Extrahiere den Hauptnamen
  const mainName = normalizedName
    .replace(/versicherung|versicherungs|aktiengesellschaft|ag|gmbh|service|rechtsschutz/gi, '')
    .split(/[\s-]+/)
    .filter(w => w.length > 2)[0];
  
  if (mainName) {
    const cleanName = mainName
      .replace(/[äöüß]/g, (match) => {
        const replacements = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' };
        return replacements[match] || match;
      })
      .replace(/[^a-z0-9]/g, '');
    
    if (cleanName.length > 2) {
      alternatives.push(`${cleanName}.com`);
      alternatives.push(`${cleanName}.de`);
      alternatives.push(`${cleanName}-versicherung.de`);
    }
  }
  
  return alternatives;
};

/**
 * InsuranceLogo Komponente
 * 
 * @param {string} name - Name der Versicherung
 * @param {string} domain - Optional: Direkte Domain der Versicherung
 * @param {string} size - Größe: 'xs' (16px), 'sm' (24px), 'md' (32px), 'lg' (48px), 'xl' (64px)
 * @param {string} className - Zusätzliche CSS-Klassen
 * @param {boolean} showFallback - Zeigt Fallback-Icon bei Fehler
 */
const InsuranceLogo = ({ 
  name, 
  domain, 
  size = 'md', 
  className = '',
  showFallback = true 
}) => {
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const sizeMap = {
    'xs': 16,
    'sm': 24,
    'md': 32,
    'lg': 48,
    'xl': 64
  };
  
  const pixelSize = sizeMap[size] || 32;
  
  // Sammle alle möglichen Domains
  const primaryDomain = domain || getDomainFromName(name);
  const alternativeDomains = getAlternativeDomains(name);
  const allDomains = [primaryDomain, ...alternativeDomains].filter(Boolean);
  
  // Reset wenn sich der Name ändert
  useEffect(() => {
    setCurrentDomainIndex(0);
    setHasError(false);
    setIsLoading(true);
  }, [name, domain]);
  
  // Clearbit Logo URL für aktuelle Domain
  const currentDomain = allDomains[currentDomainIndex];
  const logoUrl = currentDomain 
    ? `https://logo.clearbit.com/${currentDomain}?size=${pixelSize * 2}` // 2x für Retina
    : null;
  
  const handleError = () => {
    // Versuche nächste Domain
    if (currentDomainIndex < allDomains.length - 1) {
      setCurrentDomainIndex(prev => prev + 1);
      setIsLoading(true);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  };
  
  // Fallback anzeigen wenn kein Logo oder alle Domains fehlgeschlagen
  if (!logoUrl || (hasError && currentDomainIndex >= allDomains.length - 1)) {
    if (!showFallback) return null;
    
    return (
      <div 
        className={`flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg ${className}`}
        style={{ width: pixelSize, height: pixelSize }}
        title={name}
      >
        <Building2 
          className="text-blue-400" 
          style={{ width: pixelSize * 0.6, height: pixelSize * 0.6 }} 
        />
      </div>
    );
  }
  
  return (
    <div 
      className={`relative flex items-center justify-center ${className}`}
      style={{ width: pixelSize, height: pixelSize }}
      title={name}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 rounded-lg animate-pulse" />
      )}
      <img
        src={logoUrl}
        alt={`${name || 'Versicherung'} Logo`}
        className={`object-contain rounded-lg transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        style={{ width: pixelSize, height: pixelSize }}
        onLoad={() => setIsLoading(false)}
        onError={handleError}
        loading="lazy"
      />
    </div>
  );
};

/**
 * InsuranceLogoWithName - Logo mit Firmennamen daneben
 */
export const InsuranceLogoWithName = ({ 
  name, 
  domain,
  size = 'md',
  className = '' 
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <InsuranceLogo name={name} domain={domain} size={size} />
      <span className="font-medium text-gray-900 truncate">{name}</span>
    </div>
  );
};

/**
 * InsuranceCard - Vollständige Versicherungs-Anzeige mit Logo
 */
export const InsuranceCard = ({ 
  insurance,
  size = 'lg',
  className = '' 
}) => {
  if (!insurance) return null;
  
  return (
    <div className={`flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 ${className}`}>
      <InsuranceLogo 
        name={insurance.name || insurance.versicherung_gesellschaft} 
        size={size}
        className="flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 truncate">
          {insurance.name || insurance.versicherung_gesellschaft}
        </p>
        {(insurance.city || insurance.plz) && (
          <p className="text-sm text-gray-500 truncate">
            {insurance.plz} {insurance.city}
          </p>
        )}
        {insurance.phone && (
          <p className="text-sm text-gray-500">
            Tel: {insurance.phone}
          </p>
        )}
      </div>
    </div>
  );
};

export default InsuranceLogo;
